"use client";

import { useRouter } from "next/navigation";

import type { ActionMenuKit } from "@/components/ui/action-menu-kit";
import { ContextMenuItem, ContextMenuSeparator } from "@/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import type { RowOut } from "@/lib/api/model";

import { useConversationMutations } from "../../hooks/use-conversation-mutations";
import { COPY } from "../../lib/copy";
import { conversationErrorMessage } from "../../lib/error-copy";
import { CONVERSATIONS_ROUTES } from "../../lib/routes";

const R = COPY.rowActions;

/** The row's actions, shared by the kebab menu and the row's right-click menu (the jobs list pattern). */
function useRowActions(row: RowOut) {
  const router = useRouter();
  const mutations = useConversationMutations(row.id);
  const href = CONVERSATIONS_ROUTES.conversation(row.id);
  // The server decides who may edit (403 → toast); the list has no per-row flags, so the menu offers and reports.
  const run = (p: Promise<unknown>, ok: string) =>
    p.then(() => showToast.success(ok)).catch((e: unknown) => showToast.error(conversationErrorMessage(e)));
  const unresolved = row.binding == null || row.binding.level === "unresolved";
  return {
    open: () => router.push(href),
    openInNewTab: () => window.open(href, "_blank", "noopener,noreferrer"),
    copyLink: () =>
      void navigator.clipboard
        .writeText(new URL(href, window.location.origin).toString())
        .then(() => showToast.success(R.linkCopied)),
    // Only an unmatched meeting can be flagged; a flagged one can always go back to needing a match.
    toggleMatch:
      row.match_not_needed || unresolved
        ? () =>
            void run(
              mutations.patch.mutateAsync({ match_not_needed: !row.match_not_needed }),
              row.match_not_needed ? R.needsMatchDone : R.noMatchDone,
            )
        : null,
    toggleArchive: () =>
      void (row.archived_at
        ? run(mutations.unarchive.mutateAsync(), R.unarchived)
        : run(mutations.archive.mutateAsync(), R.archived)),
  };
}

function RowActionItems({ row, kit: { Item, Separator } }: { row: RowOut; kit: ActionMenuKit }) {
  const a = useRowActions(row);
  const icon = (name: Parameters<typeof Icon>[0]["name"]) => (
    <Icon name={name} size={14} className="text-heading opacity-50" />
  );
  return (
    <>
      <Item onSelect={a.open}>
        {icon("IconSquareArrowTopRight2")}
        {R.open}
      </Item>
      <Item onSelect={a.openInNewTab}>
        {icon("IconArrowUpRight")}
        {R.openInNewTab}
      </Item>
      <Item onSelect={a.copyLink}>
        {icon("IconChainLink1")}
        {R.copyLink}
      </Item>
      <Separator />
      {a.toggleMatch ? (
        <Item onSelect={a.toggleMatch}>
          {icon("IconCheckCircle2")}
          {row.match_not_needed ? COPY.header.markNeedsMatch : COPY.header.markNoMatch}
        </Item>
      ) : null}
      <Item onSelect={a.toggleArchive}>
        {icon("IconArchive")}
        {row.archived_at ? COPY.header.unarchive : COPY.header.archive}
      </Item>
    </>
  );
}

const dropdownKit: ActionMenuKit = { Item: DropdownMenuItem, Separator: DropdownMenuSeparator };
const contextKit: ActionMenuKit = { Item: ContextMenuItem, Separator: ContextMenuSeparator };

export function RowActionsContextMenuContent({ row }: { row: RowOut }) {
  return <RowActionItems row={row} kit={contextKit} />;
}

export function RowActionsMenu({ row }: { row: RowOut }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={R.label(row.title || COPY.untitled)}
          onClick={(e) => e.stopPropagation()}
          className="flex cursor-pointer items-center justify-center opacity-50 transition-opacity hover:opacity-80"
        >
          <Icon name="IconDotGrid1x3Vertical" size={16} className="text-heading" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} onClick={(e) => e.stopPropagation()}>
        <RowActionItems row={row} kit={dropdownKit} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
